import { useMemo } from "react";

import { useReactiveSelectQuery } from "@/models/hooks/useReactiveSelectQuery";
import { allTagsQuery } from "@/models/repositories/Tag";
import { allWorkspaceMembershipTagsQuery } from "@/models/repositories/WorkspaceMembershipTag";
import type { TagDefinition } from "@/types/driver";

const TAG_THROTTLE_MS = 500;

export function useDriverTags() {
  const tagRows = useReactiveSelectQuery(allTagsQuery, [], "DriverView:allTagsQuery", TAG_THROTTLE_MS) ?? [];
  const membershipTagRows =
    useReactiveSelectQuery(allWorkspaceMembershipTagsQuery, [], "DriverView:allMembershipTagsQuery", TAG_THROTTLE_MS) ??
    [];

  const { tags, tagValueMap } = useMemo(() => {
    // Disabled tags are kept in the local DB so we can track soft-deletes across incremental syncs,
    // but they must never surface in the driver view — no column, no filter, no visibility toggle.
    // Filtering once here is enough to hide them from every downstream consumer of this hook.
    const tagDefs: TagDefinition[] = tagRows
      .filter((t) => Boolean(t.enabled))
      .map((t) => ({
        id: String(t.id),
        label: String(t.label),
        multiValue: Boolean(t.multiValue),
      }));

    const valueMap = new Map<string, Record<string, string[]>>();
    for (const row of membershipTagRows) {
      const membershipId = String(row.workspaceMembershipId);
      const tagId = String(row.tagId);
      const existing = valueMap.get(membershipId) ?? {};
      existing[tagId] = [...(existing[tagId] ?? []), String(row.tagValue)];
      valueMap.set(membershipId, existing);
    }

    return { tags: tagDefs, tagValueMap: valueMap };
  }, [tagRows, membershipTagRows]);

  return { tags, tagValueMap };
}
